import React, { useState, useRef } from "react";
import api from "../lib/api";
import { checkInteractions, DDI_RULES } from "../lib/drugInteractions";
import { AlertTriangle, Sparkles, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

const DDI_STATIC_COUNT = DDI_RULES.length;

// Two-tier interaction checker: fast static matrix first, then optional Gemini
// cross-check via /api/interactions/check for anything the matrix might miss.
export default function InteractionCheckButton({ medicines, patient, known, egfr, labs, flags = {}, onDeprescribe }) {
  const [open, setOpen] = useState(false);
  const [aiHits, setAiHits] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  // Cache AI results by a hash of the current medicine list so re-opening the
  // same Rx doesn't re-charge Gemini quota. Reset only when meds change.
  const aiCache = useRef(new Map());

  const staticHits = checkInteractions(medicines, flags);
  const totalCount = staticHits.length + aiHits.length;
  const critCount = staticHits.filter((h) => h.severity === "critical").length + aiHits.filter((h) => h.severity === "critical").length;

  const medHash = (medicines || [])
    .map((m) => (m.generic_name || m.composition || m.brand_name || "").toLowerCase().trim())
    .filter(Boolean)
    .sort()
    .join("|") + "::" + JSON.stringify(flags || {});

  const runAi = async (force = false) => {
    if (!force && aiCache.current.has(medHash)) {
      setAiHits(aiCache.current.get(medHash));
      return;
    }
    setAiLoading(true); setAiError("");
    try {
      const body = {
        medicines: (medicines || []).map((m) => ({
          brand_name: m.brand_name, generic_name: m.generic_name, composition: m.composition,
          strength: m.strength, frequency: m.frequency,
        })),
        known, age: patient?.age, egfr, labs,
        patient_flags: flags,
        already_flagged: staticHits.map((h) => ({ label: h.label })),
      };
      const res = await api.post("/interactions/check", body);
      const hits = res.data?.interactions || [];
      aiCache.current.set(medHash, hits);
      setAiHits(hits);
      if (res.data?.error) setAiError(res.data.error);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || "AI check failed");
    } finally { setAiLoading(false); }
  };

  const openModal = () => {
    setAiHits([]); setAiError("");
    setOpen(true);
    // Auto-run AI cross-check when opening.
    setTimeout(runAi, 100);
  };

  const meds = (medicines || []).filter((m) => m?.brand_name || m?.generic_name || m?.composition);
  if (meds.length < 2) return null;

  const sevPill = (severity) => {
    const map = {
      critical: "bg-red-600 text-white",
      major: "bg-orange-500 text-white",
      moderate: "bg-amber-400 text-slate-900",
      minor: "bg-slate-300 text-slate-800",
    };
    return `text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${map[severity] || "bg-slate-300 text-slate-800"}`;
  };

  const summary = staticHits.length ? `${critCount ? `${critCount} critical · ` : ""}${staticHits.length} flagged` : "Check";

  return (
    <>
      <button
        onClick={openModal}
        className={`h-7 gap-1 rounded px-2.5 text-[11px] font-medium flex items-center border ${
          staticHits.length ? (critCount ? "bg-red-600 hover:bg-red-700 text-white border-red-700" : "bg-orange-500 hover:bg-orange-600 text-white border-orange-600") : "bg-white hover:bg-slate-50 text-slate-700 border-slate-300"
        }`}
        data-testid="check-interactions-btn"
        title="Scan the current prescription for drug-drug interactions (static matrix + AI cross-check)"
      >
        <AlertTriangle size={11} /> Check Interactions {staticHits.length ? `· ${summary}` : ""}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="interactions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle size={18} className="text-orange-600" />
              Drug-Drug Interaction Report
              <span className="text-[11px] font-normal text-slate-500 ml-auto">
                {totalCount === 0 ? "No interactions" : `${totalCount} finding${totalCount === 1 ? "" : "s"}`}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-4 pr-1 text-sm">
            {/* Static matrix section */}
            <section data-testid="ddi-static-section">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle size={11} /> Curated matrix ({staticHits.length})
              </div>
              {staticHits.length === 0 && (
                <div className="text-xs text-slate-500 italic border border-emerald-200 bg-emerald-50/50 rounded-md p-2" data-testid="ddi-static-clean">
                  ✓ No interactions detected by the curated matrix ({DDI_STATIC_COUNT} rules covered).
                </div>
              )}
              <div className="space-y-2">
                {staticHits.map((h) => (
                  <div key={h.key} className="border border-slate-200 rounded-md p-2.5 bg-white" data-testid={`ddi-static-${h.key}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={sevPill(h.severity)} data-testid={`ddi-severity-${h.key}`}>{h.severity}</span>
                      <span className="font-semibold text-slate-900">{h.label}</span>
                      {onDeprescribe && h.severity === "critical" && (h.left || h.groups) && (
                        <DeprescribeMenu
                          hit={h}
                          medicines={medicines}
                          onDeprescribe={(idx, note) => { onDeprescribe(idx, note, h); }}
                          testidPrefix={`ddi-deprescribe-${h.key}`}
                        />
                      )}
                    </div>
                    <div className="text-[11px] text-slate-700 mt-1"><strong>Mechanism:</strong> {h.mechanism}</div>
                    <div className="text-[11px] text-pink-800 mt-0.5"><strong>Management:</strong> {h.management}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* AI section */}
            <section data-testid="ddi-ai-section">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> AI cross-check ({aiHits.length})
                {aiLoading && <Loader2 size={11} className="animate-spin text-pink-600" />}
                {!aiLoading && (
                  <button onClick={() => runAi(true)} className="ml-auto text-[10px] text-pink-700 hover:underline" data-testid="ddi-ai-rerun">re-run</button>
                )}
              </div>
              {aiError && <div className="text-[11px] text-amber-700 italic mb-2">AI unavailable: {aiError}</div>}
              {!aiLoading && aiHits.length === 0 && !aiError && (
                <div className="text-xs text-slate-500 italic border border-emerald-200 bg-emerald-50/50 rounded-md p-2" data-testid="ddi-ai-clean">
                  ✓ AI cross-check found no additional major or critical interactions beyond the curated matrix.
                </div>
              )}
              <div className="space-y-2">
                {aiHits.map((h, i) => (
                  <div key={i} className="border border-purple-200 rounded-md p-2.5 bg-purple-50/40" data-testid={`ddi-ai-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={sevPill(h.severity)}>{h.severity}</span>
                      <span className="font-semibold text-slate-900">{h.label}</span>
                      <span className="text-[9px] text-purple-700 uppercase tracking-wider">AI</span>
                    </div>
                    {h.mechanism && <div className="text-[11px] text-slate-700 mt-1"><strong>Mechanism:</strong> {h.mechanism}</div>}
                    {h.management && <div className="text-[11px] text-pink-800 mt-0.5"><strong>Management:</strong> {h.management}</div>}
                  </div>
                ))}
              </div>
            </section>

            {totalCount === 0 && !aiLoading && (
              <div className="text-center py-8 text-emerald-800" data-testid="ddi-all-clean">
                <div className="text-3xl">✓</div>
                <div className="font-semibold mt-1">No significant drug interactions</div>
                <div className="text-[11px] text-slate-500">Both the curated matrix and AI cross-check found nothing to flag.</div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 mt-3 border-t pt-3">
            <div className="text-[10px] text-slate-500 italic mr-auto">
              Curated matrix: {DDI_STATIC_COUNT} pairs · AI cross-check via Gemini
            </div>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="ddi-close-btn"><X size={14} /> Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// end of component file

// Small dropdown that lists the specific medicine rows involved in a critical
// DDI hit and lets the doctor remove them with one click. Adds an audit note
// containing the DDI label + severity so the removal is traceable.
function DeprescribeMenu({ hit, medicines, onDeprescribe, testidPrefix }) {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  // Close the dropdown on any outside click / Escape key.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  // Figure out which rows in `medicines` matched the left/right/triple groups.
  const groups = hit.groups || (hit.left && hit.right ? [hit.left, hit.right] : []);
  const involvedIdx = new Set();
  (medicines || []).forEach((m, i) => {
    const text = `${m.generic_name || ""} ${m.composition || ""} ${m.brand_name || ""}`.toLowerCase();
    for (const g of groups) {
      if (g.some((needle) => text.includes(needle.toLowerCase()))) involvedIdx.add(i);
    }
  });
  const rows = Array.from(involvedIdx).map((i) => ({ idx: i, m: medicines[i] })).filter((x) => x.m);
  if (!rows.length) return null;

  return (
    <div className="relative ml-auto" ref={menuRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-1"
        data-testid={testidPrefix}
        title="Remove one of the offending medicines and add an audit note explaining why"
      >
        Deprescribe ▾
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-50 bg-white border border-slate-300 rounded-md shadow-lg text-xs min-w-[220px] py-1" onClick={(e) => e.stopPropagation()}>
          <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">Remove offending drug</div>
          {rows.map(({ idx, m }) => (
            <button
              key={idx}
              onClick={() => {
                const note = `Deprescribed due to DDI: ${hit.label} (${hit.severity}). Removed ${m.brand_name || m.generic_name || m.composition}.`;
                onDeprescribe(idx, note);
                setOpen(false);
              }}
              className="block w-full text-left px-2 py-1 hover:bg-red-50 flex items-center gap-1.5"
              data-testid={`${testidPrefix}-row-${idx}`}
            >
              <span className="text-red-700">✕</span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 truncate">{m.brand_name || m.generic_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{m.generic_name || m.composition}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
